import React, { useState, useEffect } from 'react';
import AppNav from './AppNav';
import AdUnit from './AdUnit';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// ─── Full article content ────────────────────────────────────────────────────
const ARTICLES = [
  {
    id: 'tomato-prices-nigeria-2025',
    category: 'Market Analysis',
    date: 'May 6, 2026',
    readTime: '6 min read',
    title: 'Why Tomato Prices in Nigeria Spike Every Dry Season — And How to Plan Ahead',
    excerpt: 'Tomatoes are among the most consumed vegetables in Nigeria, yet their prices remain among the most volatile. Understanding the seasonal pattern can save households and food businesses thousands of naira every year.',
    image: 'https://images.unsplash.com/photo-1592924357228-91a4daadcfea?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Tomatoes are among the most consumed vegetables in Nigeria, yet their prices remain among the most volatile in the country's food market. A basket of tomatoes that costs ₦3,000 in October can jump to ₦18,000 or more by February — a 500% increase within just a few months.

**The Seasonal Cycle Explained**

Nigeria's tomato supply follows a clear agricultural calendar. The main growing seasons are:

- **Rainy season harvest (July – October):** Supply peaks, prices fall. Most states in the North — Kano, Kaduna, Jigawa, Plateau — produce the bulk of Nigeria's tomatoes during this window. Markets in Lagos, Onitsha, and Abuja are flooded with affordable tomatoes.
- **Dry season (November – March):** Rain-fed farms stop producing. Only farmers with irrigation infrastructure (a small minority) continue to supply the market. This scarcity drives prices sharply upward.
- **Early planting season (April – June):** New crops are in the ground but not yet ready. Prices remain elevated until the first rains trigger a fresh harvest.

**The Role of Post-Harvest Losses**

Nigeria loses an estimated 45–50% of its tomato production to post-harvest spoilage annually, according to data from the Federal Ministry of Agriculture. This is a critical factor. Even when the harvest is abundant, poor cold chain infrastructure — a lack of refrigerated trucks, functional cold rooms, and processing facilities — means a large portion of tomatoes rot before reaching consumers.

This waste artificially shortens the supply window, making the low-price season shorter than it needs to be and the high-price season more severe.

**What the Data Shows**

Analysis of WFP market price records across 20 Nigerian cities from 2002 to 2024 shows a consistent pattern: tomato prices in the North Central and Southwest zones rise by an average of 180–320% between December and March compared to their October baseline. Cities like Maiduguri and Maiduguri tend to see the sharpest spikes due to limited road access reducing inter-state supply.

**Planning Ahead: Practical Tips**

For households and food businesses, the data suggests a clear strategy:
1. **Buy and process during peak harvest (August–October).** Home-made tomato paste, sundried tomatoes, and frozen purée can preserve the value of cheap seasonal tomatoes.
2. **Use price prediction tools.** Platforms like NaijaAgro Predict provide forward-looking price estimates based on historical data, allowing market participants to anticipate price movements weeks or months in advance.
3. **Source directly from Northern markets.** During the dry season, prices in Kano and Kaduna often remain lower than in Lagos or Port Harcourt due to proximity to remaining irrigated farms. Coordinated bulk buying from the source can reduce costs significantly.

**The Bigger Picture**

Addressing tomato price volatility in Nigeria is ultimately a supply chain problem, not just a farming problem. Investment in cold storage infrastructure, tomato paste processing factories, and irrigation expansion would have an outsized effect on price stability. Until then, knowledge and timing remain the most powerful tools available to ordinary Nigerians navigating the market.
    `,
  },
  {
    id: 'maize-price-drivers-nigeria',
    category: 'Commodity Guide',
    date: 'April 28, 2026',
    readTime: '7 min read',
    title: 'What Drives Maize Prices in Nigeria? A Deep Dive Into the Factors That Matter',
    excerpt: 'Maize (corn) is Nigeria\'s most important staple cereal — a key ingredient in human food, animal feed, and industrial starch. Here is what moves its price.',
    image: 'https://images.unsplash.com/photo-1601593346740-925612772716?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Maize (corn) is Nigeria's most important staple cereal — a key ingredient in human food, animal feed, and industrial starch. It is also one of the most price-sensitive commodities in the country. Understanding what drives maize prices is essential for farmers, feed mill operators, food manufacturers, and household consumers alike.

**1. Seasonal Harvest Cycles**

Nigeria has two main maize growing seasons:
- **First season (March planting, July–August harvest):** The main season in most of Nigeria. Prices usually fall sharply after this harvest.
- **Second season (August planting, November–December harvest):** A smaller supplementary harvest common in the Middle Belt and some Southern states.

Between January and June, before the first-season harvest, maize prices typically rise as stored stocks from the previous year are depleted. This is known as the "lean season" and is when price spikes are most common.

**2. Rainfall and Weather Shocks**

Nigeria's maize belt — spanning Benue, Kogi, Niger, Kaduna, and Plateau states — is rain-fed. Drought years produce lower yields and higher prices. Flooding, conversely, can destroy crops even during what should be a good season. Analysis of WFP price data shows that years with significant rainfall deficits in the North Central zone are followed by maize price increases of 40–70% above the seasonal norm in major consuming cities.

**3. The Poultry and Livestock Feed Demand**

Approximately 60% of commercially produced maize in Nigeria is consumed not by humans but by poultry and livestock farms. This creates a direct link between the health of Nigeria's poultry industry and maize prices. When poultry operations expand — or when corn substitutes become expensive — feed demand drives up maize prices independently of human food demand.

**4. Foreign Exchange and Import Competition**

Nigeria periodically imports maize from countries like Ukraine, Brazil, and the United States. The cost of these imports is denominated in US dollars, meaning that whenever the naira weakens significantly against the dollar, the landed cost of imported maize rises, reducing the price cushion it provides to the domestic market. The significant naira devaluations of 2023 and 2024 had a measurable upward effect on domestic maize prices.

**5. Government Policy Interventions**

The federal government has at various times released maize from the National Strategic Grain Reserve to stabilise prices during lean seasons. The effectiveness of these interventions depends heavily on timing, quantity, and distribution logistics. Poorly timed releases or insufficient volumes can have a limited effect on market prices.

**6. Transport and Logistics Costs**

Moving maize from the producing states in the North to consuming states in the South involves significant haulage costs. Fuel price increases, road deterioration, and insecurity along key transport corridors (such as the Abuja–Kaduna highway or the Plateau routes) can add meaningfully to the final market price in Southern cities.

**What This Means for Market Participants**

For farmers: the data suggests that holding maize for 2–3 months post-harvest, rather than selling immediately at harvest-time lows, often yields 30–50% better returns — provided adequate storage is available.

For buyers and processors: forward price estimates from tools like NaijaAgro Predict, combined with an awareness of the seasonal and policy factors above, enable better procurement planning and reduced exposure to price volatility.
    `,
  },
  {
    id: 'rice-market-nigeria-explained',
    category: 'Commodity Guide',
    date: 'April 15, 2026',
    readTime: '8 min read',
    title: 'Nigeria\'s Rice Market: Local vs Imported, Prices, and What to Expect in 2026',
    excerpt: 'Nigeria is Africa\'s largest rice consumer and has spent years trying to become self-sufficient. The resulting market dynamics create unique price patterns that every Nigerian consumer and trader should understand.',
    image: 'https://images.unsplash.com/photo-1536304993881-ff6e9eefa2a6?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Nigeria is Africa's largest rice consumer. With over 220 million people, the country consumes approximately 8 million metric tonnes of rice annually, making rice arguably the most politically and economically sensitive agricultural commodity in the country.

**The Local vs Imported Divide**

Nigerian rice comes from two broad sources:
- **Domestic production:** Mainly from Kebbi, Benue, Ebonyi, Anambra, Sokoto, and the Niger Delta states. Local rice (often sold as "Nigerian rice" or branded varieties like Ofada) has grown significantly in availability since the closure of land borders to rice imports in 2019.
- **Imported rice:** Historically smuggled through Benin Republic and other borders. Despite official import bans and high tariffs, smuggled foreign rice (often Thai or Indian parboiled rice) continues to appear in Nigerian markets, particularly in border states.

**Price Dynamics**

Local Nigerian rice typically trades at a premium over smuggled foreign rice in formal markets, due to:
1. Higher production and milling costs in Nigeria
2. Consumer preference patterns (some consumers prefer the texture of imported long-grain parboiled varieties)
3. Quality inconsistencies in some locally milled rice

However, foreign exchange scarcity and border security crackdowns have periodically made even smuggled rice expensive, narrowing or eliminating the price gap.

WFP market data shows that in major southern markets (Lagos, Port Harcourt, Onitsha), the price of a 50kg bag of rice fluctuated between ₦42,000 and ₦98,000 between 2023 and early 2026 — a range driven largely by naira depreciation, fuel subsidy removal (which raised haulage costs), and harvest outcomes in key producing states.

**Key Price Drivers in 2026**

Looking at the current market environment, the following factors are shaping rice prices:

- **Expanded local production:** The Kebbi-Lagos Anchor Borrowers partnership and other state-level production programmes have increased domestic rice supply, moderating some price pressure.
- **Naira stability:** The relative stabilisation of the naira exchange rate in late 2025 has reduced the pass-through of forex costs into food prices.
- **Fuel costs:** Transportation remains a major cost component. Any upward movement in diesel prices feeds directly into the price consumers pay.
- **Seasonal supply:** After the main November–December harvest, rice prices typically ease into early in the new year before beginning to rise again by March–April.

**What to Watch**

For household consumers, the March–June window tends to be the highest-price period for rice — buy ahead if storage is available. For traders, tracking the naira/dollar exchange rate and fuel prices provides early warning signals for price shifts before they appear in the market.

NaijaAgro Predict incorporates historical WFP data and seasonal patterns to provide price estimates for rice across 20+ Nigerian cities, helping both buyers and sellers make better-informed decisions.
    `,
  },
  {
    id: 'groundnut-oil-palm-oil-price-guide',
    category: 'Commodity Guide',
    date: 'April 3, 2026',
    readTime: '5 min read',
    title: 'Groundnut Oil vs Palm Oil in Nigeria: Price Trends, Regional Differences, and What Drives the Gap',
    excerpt: 'Nigeria uses both groundnut oil and palm oil extensively in cooking, but their price dynamics are very different. Here is what separates them.',
    image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Edible oils are among the most important food ingredients in Nigerian households and restaurants. Two oils dominate the market: palm oil (produced predominantly in the South) and groundnut oil (produced predominantly in the North). Their price trends diverge significantly — and for good reason.

**Palm Oil: Southern Dominance, National Reach**

Nigeria is one of Africa's largest producers of palm oil, with Edo, Cross River, Rivers, Imo, and Abia states leading production. Palm oil is extracted year-round wherever palm trees grow, making it less seasonally volatile than most other agricultural commodities.

However, palm oil prices in Nigeria have risen sharply since 2022, driven by:
- **Increased global demand** (Malaysia and Indonesia remain the world's top producers, and global prices influence Nigerian market sentiment)
- **Naira depreciation** (which raised the naira value of both exports and imports, pulling domestic prices upward)
- **Rising processing costs** (diesel-powered mills have become more expensive to operate since the fuel subsidy removal)

WFP data shows palm oil prices in Lagos rose by approximately 210% between January 2022 and January 2026 in nominal naira terms.

**Groundnut Oil: Northern Production, Premium Positioning**

Groundnut oil is produced mainly in Kano, Kaduna, Katsina, and Jigawa states. It is considered a premium cooking oil in many Nigerian households due to its lighter flavour and higher smoke point, making it preferred for certain dishes.

Its price is driven by:
- **Groundnut (peanut) harvest outcomes** — poor rains in the North directly reduce the raw material supply
- **Competition from other groundnut uses** — roasted groundnuts, groundnut paste, and Kuli-kuli production all compete with oil extraction for raw groundnuts
- **Transport costs to the South** — long haulage distances make groundnut oil systematically more expensive in Southern markets than in the North

**The Regional Price Gap**

A consistent finding in WFP market data is that groundnut oil trades at 15–35% more than palm oil in Southern cities like Lagos and Port Harcourt, while in Northern cities the gap narrows or reverses. This reflects the production geography and transport cost structures.

**Implications for Consumers and Traders**

- Consumers in the South looking to reduce food costs can substitute palm oil for groundnut oil in many recipes without meaningful nutritional loss.
- Traders with access to transport infrastructure can profit from arbitrage opportunities between Northern groundnut oil prices and Southern market rates.
- Monitoring groundnut harvest forecasts in September–October provides early signals about groundnut oil prices for the following dry season.
    `,
  },
  {
    id: 'yam-prices-seasonal-guide-nigeria',
    category: 'Market Analysis',
    date: 'March 22, 2026',
    readTime: '6 min read',
    title: 'The Seasonal Economics of Yam in Nigeria: When to Buy, When to Sell, and Why Prices Vary So Much',
    excerpt: 'Yam is a cultural and nutritional cornerstone of Nigerian food. It is also one of the most price-volatile food items in the country. Here is the complete picture.',
    image: 'https://images.unsplash.com/photo-1585032226651-759b368d7246?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Nigeria produces more yam than any other country in the world — accounting for approximately 70% of global yam production. Yet despite this abundance, yam remains an expensive food item for millions of Nigerian households, particularly during the lean months between January and July. Understanding the economics of yam pricing is one of the most practical pieces of food market knowledge a Nigerian consumer or trader can have.

**Nigeria's Yam Belt**

The primary yam-producing states are Benue, Kogi, Nasarawa, Taraba, Plateau, Enugu, and Ebonyi. These Middle Belt and Southeast states supply yam tubers to markets across the country, from Lagos to Maiduguri. The quality and variety of yam varies by zone — Benue is known for large, high-quality white yam; Enugu and Ebonyi produce varieties preferred in Southeast cooking.

**The Seasonal Price Calendar**

Yam prices follow a very predictable annual cycle:

- **August – October (New Yam Season):** This is the peak harvest period. Prices reach their annual low. New Yam Festivals in Igbo and Idoma communities coincide with this abundance. A medium-sized tuber that might cost ₦2,500–₦3,500 in July can cost ₦800–₦1,200 in August.
- **November – December:** Prices begin rising as the harvest winds down and traders start storing yam in barns.
- **January – April (Lean Season):** This is the most expensive period. Farmers and traders release stored yam slowly to maximise returns. A tuber that cost ₦1,000 in September may cost ₦4,000–₦6,000 or more.
- **May – July (Pre-harvest):** Prices peak or plateau. The previous year's stored yam is nearly exhausted. New yam is not yet ready. This is the hardest time for consumers.

**Why Yam Storage Is So Important**

Unlike grains, yam is a fresh tuber that deteriorates without proper storage. Traditional yam barns (elevated, ventilated structures) allow farmers to store yam for 4–8 months without major losses. However, barn losses of 20–40% are common, which is factored into the prices farmers set for stored yam — explaining why prices in the lean season seem dramatically higher than production costs would suggest.

**Market Intelligence for Yam**

Historical WFP data shows that yam prices in Lagos and Abuja track closely with arrival volumes from Benue and Kogi. Any disruption to the Lokoja–Abuja or Makurdi–Abuja transport corridors (flooding, road damage, security incidents) immediately creates a price spike in these cities.

For households: buying yam in bulk during September–October and storing it in a cool, dry area can reduce annual expenditure on yam by 40–60% compared to buying weekly at market prices.

For traders: tracking barn inventory levels in Benue and Kogi in early January gives early warning of how sharp the lean-season price spike will be.
    `,
  },
  {
    id: 'food-price-inflation-nigeria-2025-2026',
    category: 'Economic Context',
    date: 'March 10, 2026',
    readTime: '7 min read',
    title: 'Food Price Inflation in Nigeria: What Happened in 2025 and What to Expect in 2026',
    excerpt: 'Nigeria\'s food inflation rate has been one of the highest in Africa in recent years. Here is a clear breakdown of the causes, the current situation, and the outlook.',
    image: 'https://images.unsplash.com/photo-1611284446314-60a58ac0deb9?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Nigeria's food inflation has been a defining economic challenge since 2023. Understanding its causes — and the forces that may moderate it going forward — is important for every household, business, and policy stakeholder in the country.

**What Happened in 2023–2025**

Several major shocks converged to drive food prices sharply higher between 2023 and 2025:

**1. Fuel Subsidy Removal (May 2023)**
The removal of the petrol subsidy by the federal government immediately raised the cost of diesel and petrol. Since diesel powers irrigation pumps, farm machinery, milling equipment, cold storage, and the trucks that transport food across the country, the cost increase cascaded through the entire food supply chain. Estimated transport cost increases of 50–80% for food haulage were recorded within months of the subsidy removal.

**2. Naira Devaluation (June 2023 and Onwards)**
The unification of Nigeria's foreign exchange rates and the resulting sharp devaluation of the naira — from around ₦460/$ to over ₦1,500/$ by early 2024 — had a severe impact on any food commodity with an imported component. Fertilisers (largely imported), agrochemicals, and imported food items all became dramatically more expensive in naira terms. This cost was passed through to consumers.

**3. Insecurity in Farming Communities**
Continued insecurity in the Middle Belt (farmers-herders conflicts, banditry) reduced agricultural output in some of Nigeria's most productive states, including Benue, Plateau, and Zamfara. Displacement of farming communities reduced planted acreage and disrupted harvests.

**4. Flooding**
Repeated severe flooding events — particularly in 2022 and 2024 — destroyed crops in the Niger Delta, Kogi, Anambra, and other flood-prone zones, creating localised and sometimes national supply shortfalls.

**The Situation in 2026**

Food inflation has moderated somewhat from its 2024 peaks, driven by:
- Relative stabilisation of the naira exchange rate
- Improved domestic rice and maize production as government agricultural programmes scale up
- Some easing of global commodity prices

However, food prices remain significantly elevated compared to 2022 levels in real terms. The compounding effect of successive years of high inflation means that even a reduction in the inflation *rate* does not reduce the absolute *price level* — prices are high and declining inflation means they are rising more slowly, not falling.

**What to Expect for the Rest of 2026**

The outlook depends on:
- Exchange rate stability (a key variable)
- The 2026 rainy season harvest outcomes (normal rainfall forecasts are broadly positive)
- Diesel prices (the single biggest logistics cost driver)
- Government grain reserve releases ahead of the lean season

NaijaAgro Predict models incorporate many of these variables to generate commodity-specific price estimates for months ahead, helping market participants plan more effectively in an uncertain environment.
    `,
  },
  {
    id: 'beans-cowpea-price-nigeria',
    category: 'Commodity Guide',
    date: 'February 25, 2026',
    readTime: '5 min read',
    title: 'Beans (Cowpea) in Nigeria: Why Prices Stay High and Where to Find the Best Value',
    excerpt: 'Beans are a primary source of protein for millions of Nigerian households. Understanding why prices have surged — and how to navigate them — is now essential knowledge.',
    image: 'https://images.unsplash.com/photo-1617634754236-6baacfad8f80?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Beans (cowpea) are often described as "the poor man's meat" in Nigeria — an affordable, protein-rich staple that has fed the country for generations. In recent years, however, beans have become anything but cheap. A measure (mudu) of beans that cost ₦400 in 2020 now costs ₦1,200–₦1,800 in many Southern markets. Understanding the reasons behind this and how to find better value is crucial for households on tight budgets.

**Nigeria's Beans Production Landscape**

Nigeria is Africa's largest producer and consumer of cowpea (beans). The main producing states are Kano, Katsina, Zamfara, Sokoto, Borno, and Yobe in the North, with smaller production in some Middle Belt states. Beans are harvested once a year in most regions, with the main harvest occurring in October–November.

**Why Prices Have Risen So Much**

Several factors explain the sustained high prices:

**1. Production Constraints in the North**
Insecurity in Northwestern Nigeria (Zamfara, Sokoto, Katsina) has disrupted farming significantly since 2021. These states are among the country's biggest cowpea producers. Farmer displacement, attacks on farming communities, and the difficulty of working farmland in conflict-affected areas have reduced planted acreage and yields.

**2. Pest and Disease Pressure**
Cowpea is highly susceptible to pod-sucking insects, particularly during flowering. Poor access to affordable, quality agrochemicals — made worse by naira devaluation raising the cost of imported pesticides — has increased post-planting losses for many smallholder farmers.

**3. Storage Losses**
Cowpea is particularly vulnerable to the bruchid weevil, a storage pest that can destroy 50–100% of stored beans within months if not properly treated. Many smallholder farmers lack access to hermetic (airtight) storage bags or appropriate pesticide treatments, forcing early sale at harvest-time lows rather than selling in the lean season when prices are higher.

**4. Strong Demand with Constrained Supply**
Nigeria's population growth continues to drive demand for affordable protein. With meat prices also elevated, beans remain a sought-after alternative, supporting demand even as supply is constrained.

**Where to Find Better Value**

- **Buy during and just after the October–November harvest.** This is when prices are at their annual low, typically 30–50% below peak lean-season prices.
- **Buy directly from Northern state markets (Kano, Katsina) or from traders who source there** rather than purchasing at Southern retail markets, where multiple layers of markup add cost.
- **Use hermetic storage bags** (available from agricultural input dealers) to store a seasonal bulk purchase. These bags kill bruchid weevils without chemicals and can preserve beans in excellent condition for 12+ months.
- **Check NaijaAgro Predict's price estimates** before making large purchases to understand whether current prices are high or low relative to the seasonal baseline.
    `,
  },
  {
    id: 'cassava-garri-price-nigeria',
    category: 'Market Analysis',
    date: 'February 10, 2026',
    readTime: '6 min read',
    title: 'Garri and Cassava in Nigeria: The Price Politics of a National Staple',
    excerpt: 'Garri is to Nigeria what bread is to Europe — a cheap, filling staple that signals economic wellbeing when it is affordable and social distress when it is not. Here is the full picture.',
    image: 'https://images.unsplash.com/photo-1567306301408-9b74779a11af?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Garri — processed cassava flour — occupies a unique cultural and economic position in Nigeria. It is arguably the country's most important food security crop, consumed across all regions, ethnicities, and income levels. When garri becomes expensive, it is a sign that something is going seriously wrong with Nigeria's food economy.

**Cassava: Nigeria's Largest Crop**

Nigeria is the world's largest producer of cassava, producing over 50 million metric tonnes annually. The crop grows across most of Nigeria's agroecological zones, is drought-tolerant, and provides calories at a lower cost than almost any other food. Cassava can also be left in the ground for up to two years after maturity, effectively serving as a natural storage system.

**The Garri Processing Chain**

Fresh cassava tubers are perishable — they must be processed within 48–72 hours of harvest or they deteriorate. Processing into garri (fermentation, pressing, frying) or fufu (fermentation, wet milling) extends shelf life significantly. This processing chain, mostly done by rural women using small-scale equipment, is a major value-addition step but also a bottleneck.

The cost of garri includes:
- Cassava farm-gate price
- Processing costs (labour, firewood or gas, water)
- Transport to urban markets
- Trader markups

**Price Trends**

Garri prices have risen sharply since 2023, following the same general pattern as other food commodities — driven by fuel costs (processing and transport), naira depreciation (affecting imported inputs), and general food inflation. White garri (less processed, staple grade) remains cheaper than yellow garri (palm oil added, considered premium).

WFP data shows that yellow garri in Lagos rose from approximately ₦500/kg in early 2022 to over ₦1,400/kg by early 2026.

**Why This Matters for Food Security**

When garri becomes expensive, it is a reliable indicator of broader food system stress, because garri is what Nigerians turn to when other foods become unaffordable. Rising garri prices mean that even the last resort cheap calorie source is being stretched.

**What NaijaAgro Predict Tracks**

Our platform tracks garri and cassava prices across multiple Nigerian cities, providing trend analysis and forward-looking estimates. For households and food businesses, monitoring these prices provides an early warning system for planning household food budgets and procurement decisions.
    `,
  },
  {
    id: 'using-price-data-for-farming-decisions',
    category: 'Farmer Guide',
    date: 'January 28, 2026',
    readTime: '6 min read',
    title: 'How Nigerian Farmers Can Use Market Price Data to Make Better Decisions',
    excerpt: 'Most Nigerian smallholder farmers sell their produce immediately after harvest at the worst possible time. Here is how price data can change that calculus.',
    image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
One of the most consistently damaging patterns in Nigerian smallholder agriculture is "harvest-time distress selling." Farmers, facing immediate cash needs for school fees, debt repayments, or household expenses, sell their crops at exactly the moment when supply is highest and prices are lowest — often within days of harvest.

The resulting loss is significant. Across commodities, the difference between harvest-time prices and lean-season prices routinely ranges from 40% to 200%. A farmer who sells 10 bags of maize at ₦15,000 per bag in September could, in many years, sell those same bags for ₦22,000–₦30,000 per bag in March. That gap — if captured — could represent the difference between a marginal and a profitable farming year.

**Why Distress Selling Happens**

The problem is not that farmers are uninformed or irrational. It is structural:

1. **Immediate cash needs** cannot wait for future high prices
2. **Storage risk** — physical losses from pests, weather, and spoilage reduce the value of stored grain
3. **Capital inaccessibility** — farmers cannot borrow against stored grain to meet immediate cash needs
4. **Price uncertainty** — farmers do not know whether prices will actually rise, and by how much

Market price data addresses the last problem directly, and partially addresses the storage risk question by helping farmers judge whether the expected price rise justifies the storage cost and risk.

**Using Price Data Practically**

**1. Know the Seasonal Baseline**
Before making a storage decision, a farmer should understand the typical price trajectory for their commodity in their region. Historical data from WFP records — now made accessible through tools like NaijaAgro Predict — shows the normal pattern. For maize in Kano, for example, the data consistently shows prices 60–80% higher in April than in October.

**2. Monitor Price Forecasts**
Forward-looking price estimates, generated using machine learning models trained on historical WFP data, allow farmers to see what prices are likely to look like 1–3 months ahead. This reduces the uncertainty that often leads to premature selling.

**3. Calculate the Storage Break-Even**
If a farmer knows that storing 10 bags of maize from October to March will cost ₦5,000 in storage fees and pesticide, and that prices are forecast to rise by ₦8,000/bag, the case for storage is clear — even accounting for a 10% physical loss.

**4. Group Marketing**
Farmer cooperatives that aggregate produce can access better prices by selling in bulk to processors and large buyers, rather than selling individually to local market traders who take a large margin.

**Making Price Data Work for You**

NaijaAgro Predict provides price estimates for 30 agricultural commodities across Nigeria's major markets, with 1–3 month forward estimates based on historical seasonal patterns. The platform is free to use and designed to be accessible on mobile data-efficient connections. For farmers with access to a smartphone — an increasingly common situation — it represents a meaningful improvement in market intelligence.
    `,
  },
  {
    id: 'groundnut-oil-palm-oil-price-nigeria-2026',
    category: 'Commodity Guide',
    date: 'May 15, 2026',
    readTime: '6 min read',
    title: 'Groundnut Oil vs Palm Oil in Nigeria: Prices, Supply, and Which to Buy When',
    excerpt: 'Cooking oil is one of the most inflation-sensitive items in the Nigerian household budget. Understanding the difference between groundnut and palm oil markets — and when to buy each — can save thousands of naira annually.',
    image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Cooking oil is one of the most consistently expensive items in the Nigerian household food basket. Both groundnut oil and palm oil have seen dramatic price increases since 2023, driven by the same macroeconomic forces — naira depreciation, fuel price increases, and higher farm input costs — but with distinct supply chain dynamics that create different seasonal buying opportunities.

**Palm Oil: Nigeria's Indigenous Staple Oil**

Nigeria is one of the world's largest producers and consumers of palm oil. The country's oil palm belt runs through the Southeast, South-South, and parts of Southwest Nigeria — Edo, Delta, Cross River, Rivers, Imo, Abia, and Ondo states. Unlike most food commodities, palm oil can be stored at room temperature for months, giving traders significant flexibility in timing sales.

Key price dynamics for palm oil:
- **Peak production: March – June (main season) and September – November (secondary season).** These are the periods when fresh fruit bunches (FFB) are most abundant and crude palm oil (CPO) is cheapest.
- **Price floor:** Even at harvest time, palm oil prices are supported by strong industrial demand from soap manufacturers, food processors, and biodiesel producers who compete with household buyers.
- **Red flag indicator:** When palm oil prices rise sharply, it often signals wider food system stress, as it is used as a cheaper substitute for other cooking fats.

**Groundnut Oil: A Northern Specialty with National Reach**

Groundnut oil production is concentrated in Nigeria's groundnut belt — Kano, Katsina, Zamfara, Sokoto, and Jigawa states. The main groundnut harvest occurs in October–November. After processing, the oil is distributed nationally, with Lagos and Abuja as the largest consuming markets.

Groundnut oil tends to trade at a premium above palm oil for several reasons:
- Higher processing cost (shelling, pressing, refining)
- Perceived quality advantage for frying (higher smoke point, neutral flavour)
- Smaller production volumes relative to palm oil

Prices typically fall 15–25% in November–January as post-harvest supplies enter the market, then rise through mid-year as stocks are drawn down.

**The Import Factor**

Nigeria also imports significant volumes of soybean oil and sunflower oil, especially for industrial food processing. These imports are priced in US dollars, so naira movements directly affect their cost. The sharp naira devaluations of 2023–2024 made imported cooking oils dramatically more expensive and pushed more consumers toward domestically produced palm and groundnut oil, increasing demand pressure on the local market.

**Practical Buying Guide**

For households:
- Buy palm oil in bulk during March–June when the main season harvest is in full flow. Prices at this time are typically 20–35% below their December highs.
- For groundnut oil, November–December is the best buying window.
- Both oils store well in sealed containers in a cool, dark location for 6–12 months.

For food businesses:
- Tracking CPO ex-mill prices from the Southeast as a leading indicator gives 4–8 weeks advance notice of retail price movements in Lagos and Abuja.
- When the naira weakens significantly, move to secure domestic oil stock before import-price pass-through inflates domestic prices.

NaijaAgro Predict tracks both palm oil and groundnut oil prices across 20+ Nigerian markets, providing seasonal trend analysis to help households and businesses time their purchases more effectively.
    `,
  },
  {
    id: 'fish-protein-prices-nigeria-2026',
    category: 'Market Analysis',
    date: 'May 8, 2026',
    readTime: '5 min read',
    title: 'Fish Prices in Nigeria: Why Stockfish, Catfish, and Mackerel Markets Move Differently',
    excerpt: 'Fish is Nigeria\'s primary animal protein source, but "fish" is not a single market. Dried stockfish, frozen mackerel (titus), and farmed catfish each respond to completely different supply and demand pressures.',
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Fish is the most widely consumed animal protein in Nigeria. Unlike beef or chicken, which are concentrated among higher-income households, fish is consumed across all income levels — from the ₦200 pieces of crayfish that flavour a pot of soup to the whole dried stockfish leg shared at a family gathering. Understanding how different fish markets work is essential for household budget planning and for food traders across the country.

**The Three Main Fish Markets**

Nigeria's fish market is usefully divided into three distinct segments with different price drivers:

**1. Frozen Imported Fish (Mackerel / Titus, Herrings, Horse Mackerel)**

Frozen imported fish — dominated by Atlantic mackerel (locally called "titus"), herrings, and horse mackerel — accounts for a large share of fish consumed in urban Nigeria. This fish is caught in the Atlantic Ocean (primarily by European, Chinese, and South American fleets), frozen at sea, and shipped to Nigerian ports.

Price drivers:
- **Global catch volumes:** In years when mackerel stocks are heavily fished, export availability and prices shift.
- **Naira/dollar exchange rate:** All imported fish is dollar-denominated. Every significant naira depreciation raises the landed cost of frozen fish. The 2023–2024 naira devaluations caused frozen fish prices to roughly double in naira terms.
- **Cold chain and power costs:** Importers and distributors must maintain the cold chain. Diesel costs for generators in warehouses and trucks are a significant operational cost that feeds into retail prices.
- **Port congestion and clearing costs:** Delays at Apapa or Tin Can Island ports, and the informal costs of clearing, add meaningfully to the final price.

**2. Dried Fish (Stockfish, Smoked Catfish, Dried Crayfish)**

Dried fish is the most affordable and shelf-stable form of fish protein available to Nigerian households. Norwegian stockfish (dried cod) has been traded in Nigeria for centuries and is particularly important in Igbo cuisine. Smoked and dried catfish is produced domestically.

Key dynamics:
- Stockfish prices are strongly linked to Norwegian production levels and naira exchange rates. Nigeria is one of Norway's largest export markets for stockfish.
- Domestic smoked catfish prices track freshwater fish supply (from Nigeria's rivers, lakes, and aquaculture ponds) and the cost of smoking fuel (firewood, sawdust).
- Dried crayfish, largely produced in the Niger Delta and Cross River estuaries, is sensitive to seasonal fishing patterns and river flooding.

**3. Farmed Catfish (Fresh / Live)**

Nigeria has one of Africa's most developed freshwater aquaculture industries. Catfish farming (primarily Clarias species) is practiced across most states, with concentrations in Ogun, Lagos, Oyo, Kwara, Kogi, and the South-South. Farmed catfish provides fresh fish protein to urban markets at prices generally below imported frozen fish.

Price drivers include:
- **Feed costs:** Fish feed (often based on soybean meal, maize, and other ingredients) is the largest operating cost for catfish farmers. When maize or soybean prices rise, catfish prices follow within 3–6 months.
- **Juvenile supply:** Seasonal availability of fingerlings affects stocking density and production volumes.
- **Market seasonality:** Catfish demand peaks at festive periods (Christmas, Easter, Eid) and during August–September when boarding school seasons return and household food spending rises.

**What This Means for Consumers**

- For households managing a tight food budget, dried fish (stockfish pieces, crayfish, dried catfish) offers the best protein value per naira compared to fresh or frozen options.
- For fresh fish buyers, periods when the naira is relatively stable and aquaculture feed costs are moderate produce the most competitive catfish prices.
- Monitoring exchange rate movements provides early warning of upcoming frozen fish price increases, allowing advance bulk purchases.

NaijaAgro Predict tracks fish prices across multiple markets and formats, providing trend data and forward estimates for one of Nigeria's most important food protein categories.
    `,
  },
  {
    id: 'egg-poultry-price-nigeria-2026',
    category: 'Market Analysis',
    date: 'April 22, 2026',
    readTime: '5 min read',
    title: 'Eggs and Poultry in Nigeria: How Feed Costs, Disease, and Exchange Rates Drive Your Food Bill',
    excerpt: 'The price of a crate of eggs has become one of the most-watched household price indicators in Nigeria. Here is a complete breakdown of what drives it.',
    image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
The price of eggs in Nigeria has become a reliable barometer of the overall health of the food economy. When eggs are expensive, it signals cost pressure across the entire poultry value chain — from hatcheries and feed mills to farms and retail markets. For the millions of Nigerian households that rely on eggs as their primary or most affordable animal protein, these prices have a direct impact on nutrition and household welfare.

**Nigeria's Poultry Industry**

Nigeria has one of Africa's largest poultry industries, with an estimated 180–200 million birds (layers and broilers combined). The industry is dominated by smallholder and medium-scale commercial farmers, concentrated in Ogun, Oyo, Lagos, Kaduna, Plateau, and Enugu states.

Despite this scale, Nigeria is not self-sufficient in eggs. The country imports day-old chicks and parent stocks for breeding, as well as significant volumes of poultry vaccines and veterinary medicines — all denominated in foreign currency.

**The Feed Cost Crisis**

The single biggest driver of egg and poultry prices in Nigeria is feed cost, which represents 65–75% of total production costs for commercial layer and broiler operations.

Poultry feed is composed primarily of:
- **Maize (50–55% of feed formulation):** Domestic maize prices, as discussed elsewhere, are subject to significant seasonal and weather-related volatility.
- **Soybean meal (25–30%):** Nigeria is a large importer of soybean meal. Dollar-denominated import costs mean naira depreciation directly raises feed costs.
- **Other ingredients:** Limestone, salt, vitamins, amino acids — many of which are imported.

The combined effect of high maize prices (driven by poor harvests and lean-season scarcity) and expensive soybean meal (driven by naira depreciation) has created sustained, structural cost pressure on poultry producers since 2023. Many small-scale farmers have exited the market entirely, reducing supply and supporting higher prices.

**Disease Shocks**

Avian influenza (bird flu) outbreaks periodically devastate Nigerian poultry populations. Confirmed HPAI (Highly Pathogenic Avian Influenza) outbreaks trigger mandatory culling of affected flocks and can reduce regional egg supply by 20–40% within weeks, causing sharp price spikes. These disease events are difficult to predict but are monitored by Nigeria's Federal Department of Livestock Services.

**Seasonal Demand Patterns**

Egg demand in Nigeria rises during:
- **School resumption periods (September, January):** Boarding school demand for eggs increases significantly.
- **Festive periods (Christmas, Easter):** Households increase protein consumption.
- **Ramadan:** Egg demand from Muslim-majority Northern states shifts during fasting hours but remains elevated for Iftar and Suhur meals.

These demand peaks, layered on top of persistent supply-side cost pressures, create the conditions for price spikes.

**What Consumers Can Do**

- **Buy in bulk when prices dip.** Eggs have a shelf life of 3–5 weeks at room temperature and 6–8 weeks refrigerated. Buying a full crate when prices are seasonally low provides meaningful savings.
- **Track feed price indicators.** When maize prices in Northern markets are rising sharply (visible on NaijaAgro Predict), expect egg price increases 4–8 weeks later as higher feed costs are passed through.
- **Compare sources.** Farm-gate prices from local poultry farms are typically 10–20% below retail market prices. For regular buyers, establishing a direct farm relationship can provide savings.

Understanding the economics behind egg prices transforms what might seem like random market fluctuations into a predictable system that can be navigated with the right information.
    `,
  },
  {
    id: 'onion-pepper-price-trends-nigeria',
    category: 'Market Analysis',
    date: 'January 15, 2026',
    readTime: '5 min read',
    title: 'Onions and Peppers in Nigeria: Understanding Price Volatility for Two Essential Ingredients',
    excerpt: 'No Nigerian kitchen operates without onions and peppers. Their prices can make or break a restaurant\'s monthly food cost budget. Here is what drives them.',
    image: 'https://images.unsplash.com/photo-1518977956812-cd3dbadaaf31?w=800&q=80',
    author: 'NaijaAgro Research Desk',
    content: `
Onions and peppers are the foundation of Nigerian cooking. From jollof rice to pepper soup, egusi to tomato stew, virtually every Nigerian dish begins with these two ingredients. For food businesses — restaurants, caterers, fast food operators — onions and peppers are often among the top three food cost items. Their price volatility is therefore not a minor inconvenience but a significant business risk.

**Onions: The Kebbi-Sokoto-Kano Triangle**

Nigeria's onion production is highly concentrated. The dry, sandy soils of Kebbi, Sokoto, and Kano states, combined with irrigation from the Sokoto and Rima rivers, make this region the source of the vast majority of Nigeria's domestically produced onions.

Key price dynamics:
- **Peak harvest: December – March (dry season).** Counter-intuitively, onion harvest in Nigeria happens during the dry season, when irrigation is used. This is when onion prices are lowest.
- **Off-season: June – September (rainy season).** During the rainy season, onion farms are not productive, and stored onions from the dry-season harvest are the only domestic supply. Prices rise 80–150% from their December lows.

Onions from Niger Republic also enter Nigerian northern markets, providing some additional supply. Exchange rate movements between the naira and CFA franc affect the volume and price of these cross-border flows.

**Peppers: A More Complex Supply Chain**

Nigeria uses several types of pepper — tatase (bell pepper), tatashe, rodo (scotch bonnet/habanero), and shombo (cayenne). Each has somewhat different supply patterns, but collectively they share common price drivers:

- **High perishability.** Fresh peppers deteriorate within days without refrigeration, creating severe logistical pressure and price spikes when transport is disrupted.
- **Bimodal harvest.** Pepper can be produced in both the rainy and dry seasons with irrigation, but dry-season production costs more, raising prices.
- **High spoilage rates.** Post-harvest pepper losses of 30–50% are common due to inadequate cold chain, significantly inflating effective market prices.

**Practical Implications for Households and Food Businesses**

- **Buy peppers in bulk during peak rainy season (July–September) and dry or freeze them.** Dried or frozen peppers retain most of their flavour and nutrition and can be stored for 6–12 months.
- **Monitor Dawanau Market (Kano) onion prices** as a leading indicator for onion prices across the country — this wholesale market sets the national price benchmark.
- **Use NaijaAgro Predict** to check forward price estimates for both commodities before making large procurement decisions.

Understanding the seasonal rhythms of onion and pepper prices — and acting on that knowledge — is one of the most actionable ways Nigerian households and food businesses can reduce their food costs in the current high-inflation environment.
    `,
  },
];

// ─── Blog List View ──────────────────────────────────────────────────────────
function BlogList({ onSelect, isMobile, onNavigate }) {
  const featured = ARTICLES[0];
  const rest = ARTICLES.slice(1);

  return (
    <div>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .blog-card { transition: transform 0.2s ease, box-shadow 0.2s ease; cursor: pointer; }
        .blog-card:hover { transform: translateY(-3px); box-shadow: 8px 8px 0px rgba(10,46,20,0.12) !important; }
        .blog-featured { transition: transform 0.2s ease, box-shadow 0.2s ease; cursor: pointer; }
        .blog-featured:hover { transform: translateY(-4px); box-shadow: 12px 12px 0px rgba(212,160,23,0.15) !important; }
        .cat-badge { display: inline-block; font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; padding: 4px 10px; background: rgba(212,160,23,0.12); color: #9a7010; border: 1px solid rgba(212,160,23,0.3); font-family: 'Space Grotesk', sans-serif; }
        .ai-gen-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(212,160,23,0.45) !important; }
        .ai-gen-btn:active { transform: translateY(0); }
      `}</style>

      {/* Hero Banner */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        padding: isMobile ? '3rem 1.25rem 3.5rem' : '4rem 2rem 4.5rem',
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'rgba(212,160,23,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '20%', width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.02)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', animation: 'fadeUp 0.6s ease both' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 14 }}>
            Market Knowledge Hub
          </div>
          <h1 style={{
            fontFamily: 'Epilogue, sans-serif',
            fontSize: isMobile ? 'clamp(2rem, 9vw, 2.8rem)' : 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1.05,
            marginBottom: 16, maxWidth: 700,
          }}>
            Nigeria Agricultural<br />
            <span style={{ color: '#d4a017' }}>Market Intelligence</span>
          </h1>
          <p style={{ fontSize: isMobile ? 13 : 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, maxWidth: 540, marginBottom: 24 }}>
            In-depth guides on commodity prices, seasonal trends, and market dynamics — everything you need to make smarter food and farming decisions in Nigeria.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '2rem 1.25rem' : '3rem 2rem' }}>

        {/* Featured Article */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 12 }}>Featured Article</div>
          <div
            className="blog-featured"
            onClick={() => onSelect(featured)}
            style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              background: '#ffffff', border: '1px solid rgba(10,46,20,0.1)',
              boxShadow: '8px 8px 0px rgba(10,46,20,0.06)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              height: isMobile ? 200 : 'auto', minHeight: isMobile ? 'unset' : 300,
              backgroundImage: `url(${featured.image})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
            }} />
            <div style={{ padding: isMobile ? '1.5rem' : '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                <span className="cat-badge">{featured.category}</span>
                <span style={{ fontSize: 11, color: '#9a9f98', fontFamily: 'Manrope, sans-serif' }}>{featured.date} · {featured.readTime}</span>
              </div>
              <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 18 : 24, fontWeight: 900, color: '#001805', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 12 }}>
                {featured.title}
              </h2>
              <p style={{ fontSize: 13, color: '#424841', lineHeight: 1.7, marginBottom: 20 }}>{featured.excerpt}</p>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#0a2e14', fontFamily: 'Space Grotesk, sans-serif' }}>
                Read Full Article
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </div>
            </div>
          </div>
        </div>

        <AdUnit slot="9095850875" format="fluid" layoutKey="-fb+5w+4e-db+86" style={{ margin: '0 0 2rem 0' }} requiresContent={false} />

        {/* Article Grid */}
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 16 }}>More Articles</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 18 }}>
          {rest.map((article, i) => (
            <div
              key={article.id}
              className="blog-card"
              onClick={() => onSelect(article)}
              style={{
                background: '#ffffff', border: '1px solid rgba(10,46,20,0.08)',
                boxShadow: '5px 5px 0px rgba(10,46,20,0.05)',
                overflow: 'hidden',
                animation: `fadeUp 0.5s ease both`,
                animationDelay: `${i * 0.07}s`,
              }}
            >
              <div style={{ height: 160, backgroundImage: `url(${article.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  <span className="cat-badge">{article.category}</span>
                  <span style={{ fontSize: 10, color: '#9a9f98', fontFamily: 'Manrope, sans-serif' }}>{article.readTime}</span>
                </div>
                <h3 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: 15, fontWeight: 800, color: '#001805', letterSpacing: '-0.01em', lineHeight: 1.25, marginBottom: 8 }}>
                  {article.title}
                </h3>
                <p style={{ fontSize: 12, color: '#5a5f58', lineHeight: 1.65, marginBottom: 14 }}>
                  {article.excerpt.substring(0, 100)}...
                </p>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0a2e14', fontFamily: 'Space Grotesk, sans-serif', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Read More <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Article Detail View ─────────────────────────────────────────────────────
function ArticleDetail({ article, onBack, onNavigate, isMobile }) {
  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} style={{ height: 12 }} />;
      if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.slice(2, -2).includes('**')) {
        return (
          <h3 key={i} style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 16 : 18, fontWeight: 800, color: '#001805', margin: '1.5rem 0 0.5rem', letterSpacing: '-0.01em' }}>
            {trimmed.slice(2, -2)}
          </h3>
        );
      }
      // Inline bold: replace **text** within a line
      const parts = trimmed.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j} style={{ color: '#001805', fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      if (trimmed.startsWith('- ')) {
        return (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
            <span style={{ color: '#d4a017', flexShrink: 0, marginTop: 2 }}>▸</span>
            <p style={{ fontSize: isMobile ? 14 : 15, color: '#3a3f38', lineHeight: 1.8, margin: 0 }}>
              {trimmed.slice(2).split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
                part.startsWith('**') && part.endsWith('**')
                  ? <strong key={j} style={{ color: '#001805' }}>{part.slice(2, -2)}</strong>
                  : part
              )}
            </p>
          </div>
        );
      }
      return (
        <p key={i} style={{ fontSize: isMobile ? 14 : 15, color: '#3a3f38', lineHeight: 1.85, marginBottom: 4 }}>
          {parts}
        </p>
      );
    });
  };

  return (
    <div>
      {/* Back button + hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
        padding: isMobile ? '1.5rem 1.25rem 2rem' : '2rem 2rem 3rem',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <button
            onClick={onBack}
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: '#ffffff', padding: '8px 16px', cursor: 'pointer',
              fontFamily: 'Space Grotesk, sans-serif', fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Back to Blog
          </button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 10px', background: 'rgba(212,160,23,0.15)', color: '#d4a017', border: '1px solid rgba(212,160,23,0.3)', fontFamily: 'Space Grotesk, sans-serif' }}>{article.category}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'Manrope, sans-serif' }}>{article.date} · {article.readTime}</span>
          </div>
          <h1 style={{
            fontFamily: 'Epilogue, sans-serif',
            fontSize: isMobile ? 'clamp(1.5rem, 7vw, 2.2rem)' : 'clamp(1.8rem, 3vw, 2.6rem)',
            fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 16,
          }}>
            {article.title}
          </h1>
          <p style={{ fontSize: isMobile ? 13 : 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>{article.excerpt}</p>
          <div style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}>
            By {article.author}
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div style={{ width: '100%', height: isMobile ? 200 : 340, backgroundImage: `url(${article.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />

      {/* Article Body */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '2rem 1.25rem' : '3rem 2rem' }}>
        <div style={{
          padding: isMobile ? '1.25rem' : '2rem', background: '#f8f6f3',
          border: '1px solid rgba(10,46,20,0.08)', marginBottom: '2rem',
          borderLeft: '4px solid #d4a017',
        }}>
          <p style={{ fontSize: isMobile ? 14 : 15, color: '#424841', lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>{article.excerpt}</p>
        </div>

        {renderContent(article.content)}

        <AdUnit slot="9095850875" format="fluid" layoutKey="-fb+5w+4e-db+86" style={{ margin: '2rem 0' }} requiresContent={false} />

        {/* CTA */}
        <div style={{
          background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
          padding: isMobile ? '1.5rem' : '2rem',
          marginTop: '2rem', borderTop: '3px solid #d4a017',
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 8 }}>Use the Tool</div>
          <p style={{ fontSize: isMobile ? 14 : 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: 16 }}>
            Check current and predicted prices for this and 29 other agricultural commodities across Nigeria with NaijaAgro Predict.
          </p>
          <button
            onClick={() => onNavigate('predict')}
            style={{
              background: '#d4a017', color: '#001805', border: 'none',
              padding: '11px 22px', cursor: 'pointer',
              fontFamily: 'Space Grotesk, sans-serif', fontSize: 13, fontWeight: 700,
              boxShadow: '4px 4px 0px rgba(0,0,0,0.2)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 0px rgba(0,0,0,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '4px 4px 0px rgba(0,0,0,0.2)'; }}
          >
            Get Price Predictions →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main BlogPage Export ────────────────────────────────────────────────────
export default function BlogPage({ priceMode = 'student', onModeToggle, onNavigate, user = null, onUserChange }) {
  const isMobile = useIsMobile();
  const navH = isMobile ? 60 : 72;
  const [selected, setSelected] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, [selected]);

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
      `}</style>

      <AppNav
        activePage="blog"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />
      <div style={{ height: navH }} />

      {selected
        ? <ArticleDetail article={selected} onBack={() => setSelected(null)} onNavigate={onNavigate} isMobile={isMobile} />
        : <BlogList onSelect={setSelected} isMobile={isMobile} onNavigate={onNavigate} />
      }

      <footer style={{
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
        color: '#fff', padding: '1.75rem 2rem',
        borderTop: '3px solid #d4a017', marginTop: 0,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 800, fontSize: 15, color: '#d4a017' }}>NaijaAgro Predict</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {['Home', 'Blog', 'About', 'Terms'].map(l => (
              <button key={l} onClick={() => onNavigate && onNavigate(l.toLowerCase())} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Manrope, sans-serif', padding: 0, transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#d4a017'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
              >{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>© 2026 NaijaAgro Predict. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}